/*
* Copyright (C) Regione Lombardia
*/
import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CoreI18nConfigs } from '@core/configs/i18n.config';
import { DatalistAnagrafica, Monitoraggio } from '@models';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AnagraficaService } from '@shared/services/anagrafica.service';
import { OperaService } from '@shared/services/opera.service';
import { UtilsService } from '@shared/services/utils.service';
import { CurrencyMaskInputMode } from 'ngx-currency';
import { ModalInfoComponent } from '../modal-info/modal-info.component';
import { Subscription } from 'rxjs/internal/Subscription';
import { startWith } from 'rxjs';
import {Rilevatore} from "../../../../_models/domain/rilevatore";
import {RilevatoreService} from "@shared/services/rilevatore.service";

@Component({
    selector: 'app-toolkit-opera-modal-monitoraggi-sopralluoghi',
    templateUrl: './modal-monitoraggi-sopralluoghi.component.html'
})
export class ToolkitGestioneOperaModalMonitoraggiSopralluoghiComponent implements OnInit {
    @Input() canEdit: boolean;
    @Input() azione: string;
    @Input() params: Monitoraggio;
    @Input() datalist: DatalistAnagrafica;
    @Input() formGroup: FormGroup;

    public criteriaForm: FormGroup;
    readonly natural = CurrencyMaskInputMode.NATURAL;
    public entitaDannoLabel: string = '-';
    public isStraordinario: boolean = false;
    public isAltroTipoEvento: boolean = false; 
    private _subs: Subscription = new Subscription();

    public efficaciaDescriptionsList = [
        { title: 'Ottima', text: 'L’opera è realizzata a regola d’arte e in perfetto stato di conservazione.' },
        { title: 'Buona', text: 'L’opera svolge le funzioni per le quali è stata costruita in modo buono.' },
        { title: 'Sufficiente', text: 'L’opera svolge le funzioni in modo sufficiente.' },
        { title: 'Insufficiente', text: 'L’opera non è più adatta allo scopo per il quale è stata costruita.' },
        { title: 'Pessima', text: 'L’opera costituisce un aggravamento della situazione.' }
    ];

    public efficienzaDescriptionsList = [
        { title: 'Ottima', text: 'L’opera è realizzata a regola d’arte e in perfetto stato di conservazione.' },
        { title: 'Buona', text: 'L’opera sta subendo un processo di deterioramento dovuto a necessità di manutenzione ordinaria e/o straordinaria e/o presenta danni di lieve o modesta entità.' },
        { title: 'Sufficiente', text: 'L’opera sta subendo un processo di deterioramento dovuto a necessità di manutenzione ordinaria e/o straordinaria e/o presenta localmente danni di rilevante entità che non ne compromettono la funzionalità generale.' },
        { title: 'Insufficiente', text: 'L’opera non svolge più le sue funzioni e/o presenta danni di rilevante entità che ne compromettono la funzionalità generale.' },
        { title: 'Pessima', text: 'L’opera è in condizioni di equilibrio limite (prossima al collasso o una variazione anche modesta delle condizioni esterne potrebbe determinarne la distruzione – ad esempio una briglia o un argine fortemente scalzati).' },
        { title: 'Non determinabile', text: 'Non è possibile determinare o valutare lo stato dell’opera.' }
    ];

    public rilevatori: Rilevatore[] = [];
    public showEnteTooltip = false;

    constructor(
        public activeModal: NgbActiveModal,
        private operaService: OperaService,
        private modalService: NgbModal,
        private utilsService: UtilsService,
        private anagraficaService: AnagraficaService,
        private fb: FormBuilder,
        private cdr: ChangeDetectorRef,
        private rilevatoreService : RilevatoreService
    ) { }

    ngOnInit() {
        this.criteriaForm = this.fb.group({
            idMonitoraggiSopralluoghi: this.fb.control(null),
            idopera: this.fb.control(this.operaService.operaSession.idOpera, Validators.required),
            idtipointervento: this.fb.control(null),
            efficienza: this.fb.control(null),
            efficacia: this.fb.control(null),
            enteesecutore: this.fb.control(null),
            dataprossimaispezione: this.fb.control(null),
            linkschedamonitoraggio: this.fb.control(null),
            note: this.fb.control(null),
            idmanutenzione: this.fb.control(null),
            idfonte: this.fb.control(null),
            dataesecuzioneRilevamento: this.fb.control(null),
            idRilevatore: this.fb.control(null),
            enterilevatore: this.fb.control(null),
            idtipoevento: this.fb.control(null),
            dataevento: this.fb.control(null), 
            descrizioneevento: this.fb.control(null),
            descrizionedanno: this.fb.control(null),
            entitadanno: this.fb.control(null),
            idtipologia: this.fb.control(null),
            propostaintervento: this.fb.control(null),
            richiestafinanziamento: this.fb.control(null),
            old: this.fb.control(null),
            idrilevamento: this.fb.control(null),
            idinterventoMonitoraggio: this.fb.control(null),
            altrotipoevento: this.fb.control(null),
        });
        if (this.params) {
            this.criteriaForm.patchValue(this.params);
        }
        if (!this.canEdit) {
            this.criteriaForm.disable();
        }
        if (this.azione === 'Visualizza') {
            const idDanno = this.criteriaForm.get('entitadanno')?.value;
            if (idDanno) {
                this.getDescrizioneLivelloDanno(idDanno);
            }
        }

        this.initTipologiaEventControl();
        this.initAltroTipoEventoControl(); 
        this.initRilevatoreControl();
        this.loadRilevatori();

    }

    public saveMonitoraggiSopralluoghi() {
        if (this.criteriaForm.invalid) {
            this.utilsService.showError(CoreI18nConfigs.messages.fieldRequired);
            return;
        }

        const payload = this.criteriaForm.getRawValue();

        this.activeModal.close(payload);
    }


    public getDescrizioneLivelloDanno(idDanno: string): void {
        this.anagraficaService.getDescrizioneLivelloDanno(idDanno).subscribe({
            next: (res) => {
                this.entitaDannoLabel = res.descrizione;
                this.cdr.detectChanges();
            },
            error: (err) => {
                console.error('Errore nel recupero della descrizione:', err);
                this.entitaDannoLabel = '-';
            }
        });
    }

    getEfficaciaInfoSelected(): void {
        const modalRef = this.modalService.open(ModalInfoComponent, { size: 'lg', centered: true });
        modalRef.componentInstance.descriptions = this.efficaciaDescriptionsList;
    }

    getEfficienzaInfoSelected(): void {
        const modalRef = this.modalService.open(ModalInfoComponent, { size: 'lg', centered: true });
        modalRef.componentInstance.descriptions = this.efficienzaDescriptionsList;
    }


    private initTipologiaEventControl(): void {
        const idTipologiaCtrl = this.criteriaForm.get('idtipologia');
        if (!idTipologiaCtrl) {
            return;
        }

        const eventControls = [
            'idtipoevento',
            'dataevento',
            'entitadanno',
            'descrizioneevento',
            'descrizionedanno'
        ];

        this._subs.add(
            idTipologiaCtrl.valueChanges.pipe(startWith(idTipologiaCtrl.value)).subscribe(val => {
                const descr = this.datalist?.tipologiaRilevamento?.find(x => x.id === val)?.descrizione?.toLowerCase();
                const valStr = String(val ?? '').toLowerCase();
                const isOrdinario = (descr === 'ordinario') || (valStr === 'ordinario') || (valStr === '1');

                if (isOrdinario) {
                    eventControls.forEach(name => {
                        const c = this.criteriaForm.get(name);
                        if (!c) { return; }
                        c.disable({ emitEvent: false });
                        c.clearValidators();
                        c.setValue(null, { emitEvent: false });
                        c.markAsPristine({ onlySelf: true });
                        c.markAsUntouched({ onlySelf: true });
                        c.updateValueAndValidity({ emitEvent: false });
                    });
                } else {
                    eventControls.forEach(name => {
                        const c = this.criteriaForm.get(name);
                        if (!c) { return; }
                        if (this.canEdit && ['idtipoevento', 'dataevento', 'descrizioneevento', 'entitadanno', 'descrizionedanno'].includes(name)) {
                            c.enable({ emitEvent: false });
                            c.setValidators([Validators.required]);
                        } else {
                            c.clearValidators();
                        }
                        c.updateValueAndValidity({ emitEvent: false });
                    });
                }
            })
        );
    }
getFunzionePrevalenteDescrizione(id: number | string) {
        return this.datalist?.funzionePrevalente?.find(i => i.id === id)?.descrizione || '';
    }

    getMacroTipologiaOperaDescrizione(id: number | string) {
        return this.datalist?.macroTipologiaOpera?.find(i => i.id === id)?.descrizione || '';
    }

    getTipologiaOperaDescrizione(id: number | string) {
        return this.datalist?.tipologiaOpera?.find(i => i.idTipologiaOpera === id)?.descrizioneTipologiaOpera || '';
    }

    getMaterialeDescrizione(id: number | string) {
        return this.datalist?.materiale?.find(i => i.id === id)?.descrizione || '';
    }

    loadRilevatori(): void {
        // usa il service corretto: AnagraficaService, OperaService, ecc.
        this.rilevatoreService.getRilevatori().subscribe({
            next: (res: Rilevatore[]) => {
                this.rilevatori = res || [];
            },
            error: err => {
                console.error('Errore caricamento rilevatori', err);
            }
        });
    }

    initRilevatoreControl(): void {
        const rilevatoreCtrl = this.criteriaForm.get('idRilevatore');
        const enteRilCtrl    = this.criteriaForm.get('enterilevatore');

        if (!rilevatoreCtrl || !enteRilCtrl) {
            return;
        }

        if (!this.canEdit) {
            enteRilCtrl.disable({ emitEvent: false }); 
            this.showEnteTooltip = false;
            return;
        }

        enteRilCtrl.disable({ emitEvent: false });
        this.showEnteTooltip = false;

        this._subs.add(
            rilevatoreCtrl.valueChanges
                .pipe(startWith(rilevatoreCtrl.value))
                .subscribe((val: Rilevatore | null) => {
                    const id = val?.idRilevatore;

                    if (id === 1) {
                        enteRilCtrl.enable({ emitEvent: false });
                        this.showEnteTooltip = true;
                    } else {
                        enteRilCtrl.disable({ emitEvent: false });
                        enteRilCtrl.reset(null, { emitEvent: false });
                        this.showEnteTooltip = false;
                    }
                })
        );
    }


    private initAltroTipoEventoControl(): void {
        const tipoCtrl = this.criteriaForm.get('idtipoevento');
        const altroCtrl = this.criteriaForm.get('altrotipoevento');
        if (!tipoCtrl || !altroCtrl) { return; }

        this._subs.add(
            tipoCtrl.valueChanges.pipe(startWith(tipoCtrl.value)).subscribe(val => {
                const selectedDescr = this.datalist?.evento?.find(e => e.id === val)?.descrizione?.toLowerCase();
                const isAltro = (selectedDescr === 'altro') || (String(val || '').toLowerCase() === 'altro');
                this.isAltroTipoEvento = !!isAltro;

                if (isAltro) {
                    if (this.canEdit) {
                        altroCtrl.enable({ emitEvent: false });
                        altroCtrl.setValidators([Validators.required]);
                    } else {
                        altroCtrl.disable({ emitEvent: false });
                        altroCtrl.clearValidators();
                    }
                } else {
                    altroCtrl.disable({ emitEvent: false });
                    altroCtrl.reset(null, { emitEvent: false });
                    altroCtrl.clearValidators();
                }

                altroCtrl.updateValueAndValidity({ emitEvent: false });
            })
        );
    }

    compareRilevatore(r1: Rilevatore | null, r2: Rilevatore | null): boolean {
        if (r1 == null && r2 == null) {
            return true;
        }
        if (r1 == null || r2 == null) {
            return false;
        }
        return r1.idRilevatore === r2.idRilevatore;
    }

}